"use client";

import * as React from "react";
import { Info } from "lucide-react";
import { cn } from "../../lib/utils";

export interface InfoTooltipProps {
  content: string;
  className?: string;
  contentClassName?: string;
  align?: "left" | "right" | "center";
}

export function InfoTooltip({
  content,
  className,
  contentClassName,
  align = "left",
}: InfoTooltipProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Close tooltip on click or touch outside
  React.useEffect(() => {
    if (!isOpen) return;

    const handleOutsideInteraction = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideInteraction);
    document.addEventListener("touchstart", handleOutsideInteraction);

    return () => {
      document.removeEventListener("mousedown", handleOutsideInteraction);
      document.removeEventListener("touchstart", handleOutsideInteraction);
    };
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen((prev) => !prev);
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative inline-flex items-center select-none z-45 pointer-events-auto", className)}
      onPointerEnter={(e) => {
        if (e.pointerType === "mouse") {
          setIsOpen(true);
        }
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === "mouse") {
          setIsOpen(false);
        }
      }}
    >
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center justify-center text-[#98A2B3] hover:text-[#344054] active:text-[#101828] transition-colors focus:outline-none p-0.5 rounded cursor-pointer"
        aria-label="Info"
      >
        <Info className="w-3.5 h-3.5" />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute bottom-full z-50 mb-2 w-60 rounded-lg bg-[#101828] p-3 text-left text-xs font-normal leading-relaxed text-white shadow-xl animate-in fade-in slide-in-from-bottom-1 duration-150 pointer-events-auto",
            align === "left" && "left-0",
            align === "right" && "right-0",
            align === "center" && "left-1/2 -translate-x-1/2",
            contentClassName
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {content}
          <div
            className={cn(
              "absolute top-full -translate-y-px border-4 border-transparent border-t-[#101828]",
              align === "left" && "left-3",
              align === "right" && "right-3",
              align === "center" && "left-1/2 -translate-x-1/2"
            )}
          />
        </div>
      )}
    </div>
  );
}
